import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AccountActivePage, AccountPageExpandedSection } from './types'
import type { AccountPageUIProps } from './types'
import { AccountPageUIContext, AccountPageUIProvider, useAccountPageUIContext } from './AccountPageProvider'
import { DesktopSidebar, MobileBottomNav } from './sub-ui/AccountPageSidebar'
import ProfilePageUI from './sub-ui/ProfilePageUI'
import OrdersPageUI, { CancelDialog } from './sub-ui/OrdersPageUI'
import AccountEmailFlowOverlay from './sub-ui/AccountEmailFlowOverlay'
import { CancelDialogCtx, CancelTarget } from './sub-ui/CancelDialogContext'
import CustomPageUI from './sub-ui/CustomPageUI'
import { useAccountCustomPages } from './hooks/useAccountCustomPages'

// Matches the @[768px] container query that switches the mobile/desktop trees
const DESKTOP_MIN_WIDTH = 768

type LayoutTree = 'mobile' | 'desktop'

type EmailFlowState = { open: false } | { open: true; returnToOrders: boolean; initialEmail?: string }

const AccountPageUIComponent = () => {
  const ctx = useAccountPageUIContext()
  const [emailFlow, setEmailFlow] = useState<EmailFlowState>({ open: false })
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null)

  /**
   * modifiedCtx overrides onOpenEmailFlow so the internal email overlay
   * opens when child components call it, instead of propagating to the consumer.
   */
  const modifiedCtx: typeof ctx = {
    ...ctx,
    actions: {
      ...ctx.actions,
      onOpenEmailFlow: ({ returnToOrders, initialEmail }) => {
        setEmailFlow({ open: true, returnToOrders, initialEmail })
      },
    },
  }

  const { uiConfig: config, activePage, actions } = modifiedCtx
  const isInIframe = (() => { try { return window.self !== window.top } catch { return true } })()

  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const mobileNavRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const customPages = useAccountCustomPages()
  const activeCustomPage = customPages.find(page => page.type === activePage)

  // Both layout trees stay mounted (one hidden by CSS). Merchant HTML must mount in
  // only the visible one, or its scripts would run twice and duplicate element ids.
  const [visibleTree, setVisibleTree] = useState<LayoutTree | null>(null)
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setVisibleTree(el.offsetWidth >= DESKTOP_MIN_WIDTH ? 'desktop' : 'mobile')
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Report content height to the parent script so it can resize the iframe as
  // content is added (paginated orders) or removed (tab switch). We measure the
  // inner content element, NOT document/root: the root has min-h-screen so it
  // can only ever match the iframe height, which would ratchet upward forever.
  // The inner element's offsetHeight grows AND shrinks with actual content.
  useEffect(() => {
    if (!isInIframe || !contentRef.current) return
    let lastHeight = 0
    const report = () => {
      const el = contentRef.current
      if (!el) return
      const height = Math.ceil(el.offsetHeight)
      if (height === lastHeight) return
      lastHeight = height
      window.parent.postMessage({ type: 'FLO_ACCOUNTS_RESIZE', payload: { height } }, '*')
    }
    report()
    const ro = new ResizeObserver(report)
    ro.observe(contentRef.current)
    return () => ro.disconnect()
  }, [isInIframe])

  // Since the iframe is content-height, CSS position:sticky can never engage
  // (this document has no scroll). The parent script reports the iframe's
  // viewport offset on scroll and we pin the nav/sidebar with a transform.
  // Smoothness notes: the parent already rAF-throttles its messages, so we
  // apply immediately (an extra rAF here just adds a frame of lag); base
  // positions come from the offsetTop chain, which is layout-based and
  // unaffected by in-flight transforms; a short CSS transition on the wrappers
  // turns the inherent one-frame trail into a smooth float instead of jitter.
  useEffect(() => {
    if (!isInIframe) return
    // Document-position of an element via the offsetParent chain — this page
    // never scrolls, so this equals its untransformed viewport position.
    const docTop = (el: HTMLElement) => {
      let top = 0
      let node: HTMLElement | null = el
      while (node) {
        top += node.offsetTop
        node = node.offsetParent as HTMLElement | null
      }
      return top
    }
    const applyPin = (el: HTMLDivElement | null, gap: number, iframeTop: number) => {
      // offsetParent is null when the element is display:none at this breakpoint
      if (!el || el.offsetParent === null) {
        if (el) el.style.transform = ''
        return
      }
      const baseTop = docTop(el)
      let translate = Math.max(0, gap - (iframeTop + baseTop))
      const container = containerRef.current
      if (container) {
        // Never push the pinned element past the bottom of the page content
        const maxTranslate = container.offsetHeight - baseTop - el.offsetHeight - 16
        translate = Math.min(translate, Math.max(0, maxTranslate))
      }
      el.style.transform = translate > 0 ? `translateY(${translate}px)` : ''
    }
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (data?.type !== 'FLO_ACCOUNTS_SCROLL' || typeof data?.payload?.top !== 'number') return
      applyPin(mobileNavRef.current, 0, data.payload.top)
      applyPin(sidebarRef.current, 24, data.payload.top)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [isInIframe])

  const renderPageContent = (tree: LayoutTree) => (
    <>
      {activePage === AccountActivePage.PROFILE && <ProfilePageUI />}
      {activePage === AccountActivePage.ORDERS && <OrdersPageUI visibleLayout={visibleTree === tree ? tree : undefined} />}
      {activeCustomPage && visibleTree === tree && <CustomPageUI key={activeCustomPage.type} page={activeCustomPage} />}
    </>
  )

  return (
    <CancelDialogCtx.Provider value={{ openCancel: setCancelTarget }}>
    <AccountPageUIContext.Provider value={modifiedCtx}>
      <div
        ref={containerRef}
        className={`ui-@container ui-relative ui-w-full ui-flex ui-flex-col ${isInIframe ? 'ui-min-h-screen' : 'ui-h-full ui-overflow-hidden'}`}
        style={{
          backgroundColor: config.layout.background_color,
          borderRadius: config.layout.page_radius,
        }}
      >
        {/* Main layout — relative so the cancel dialog can be absolutely positioned within it.
            In iframe mode this element's natural height is what we report to the parent. */}
        <div ref={contentRef} className={`ui-relative ui-flex ui-flex-col ${isInIframe ? '' : 'ui-flex-1 ui-overflow-hidden'}`}>

          {/* Mobile top nav — hidden at ≥768px; pinned via transform while the parent page scrolls (iframe mode) */}
          <div ref={mobileNavRef} className={`@[768px]:ui-hidden ${isInIframe ? 'ui-relative ui-z-30 ui-will-change-transform ui-transition-transform ui-duration-150 ui-ease-out' : ''}`}>
            <MobileBottomNav />
          </div>

          {/* Mobile content — hidden at ≥768px; scrolls internally only outside the iframe */}
          <div className={`@[768px]:ui-hidden scrollbar-hide ${isInIframe ? '' : 'ui-overflow-y-auto ui-min-h-0'}`}>
            {renderPageContent('mobile')}
          </div>

          {/* Desktop: sidebar + right section — hidden below 768px */}
          <div
            className={`ui-hidden @[768px]:ui-flex @[768px]:ui-py-[3rem] ${isInIframe ? '' : 'ui-min-h-0'}`}
            style={{ margin: `0 ${config.layout.side_margin}` }}
          >
            {/* Sidebar wrapper — pinned via transform in iframe mode; display:contents otherwise */}
            <div ref={sidebarRef} className={isInIframe ? 'ui-shrink-0 ui-self-start ui-z-20 ui-will-change-transform ui-transition-transform ui-duration-150 ui-ease-out' : 'ui-contents'}>
              <DesktopSidebar />
            </div>
            {/* Right section: flex-col so footer is at bottom of this column */}
            <div className={`ui-flex-1 ui-flex ui-flex-col ${isInIframe ? '' : 'ui-min-h-0'}`}>
              <div className={`scrollbar-hide ${isInIframe ? '' : 'ui-flex-1 ui-overflow-y-auto ui-min-h-0'}`}>
                {renderPageContent('desktop')}
              </div>
            </div>
          </div>

          {/* Cancel dialog — absolutely positioned inside this relative container */}
          {cancelTarget && (
            <CancelDialog
              orderNumber={cancelTarget.orderNumber}
              color={cancelTarget.color}
              onClose={() => setCancelTarget(null)}
              onSubmit={async (note) => {
                const res = await actions.onCancelOrder?.(cancelTarget.id, note)
                if (res?.success) cancelTarget.onSuccess?.()
                return res ?? { success: false, errorMessage: 'Action not supported' }
              }}
            />
          )}

          {/* Email flow overlay — same absolute positioning as cancel dialog */}
          {emailFlow.open && (
            <AccountEmailFlowOverlay
              returnToOrders={emailFlow.returnToOrders}
              initialEmail={emailFlow.initialEmail}
              onClose={() => setEmailFlow({ open: false })}
              onComplete={(returnToOrders) => {
                setEmailFlow({ open: false })
                if (returnToOrders) {
                  ctx.actions.onTabChange(AccountPageExpandedSection.ORDERS)
                }
              }}
            />
          )}
        </div>

      </div>
    </AccountPageUIContext.Provider>
    </CancelDialogCtx.Provider>
  )
}

const AccountPageUI = (props: AccountPageUIProps) => {
  return (
    <AccountPageUIProvider {...props}>
      <AccountPageUIComponent />
    </AccountPageUIProvider>
  )
}

export default AccountPageUI
